<template>
	<div class="category-editor">

		<div class="editor-row-half editor-row-category-first">
			<div class="editor-row-half padding-large-right">
				<AppTextInput ref="name-input" class="editor-input" :label="getString('name')" :placeholder="getString('name_category_placeholder')" :value="category.name" @changed-text="(value) => category.name = value" />
				<AppColorPicker :label="getString('color')" :value="category.color ? '#'+category.color.replace('#', '') : ''" @changed-color="(v) => category.color = '#'+v.replace('#', '')" />
			</div>

			<div class="editor-row-half padding-large-left">
				<div class="editor-column mobile-40">
					<AppDropzone
						ref="upload-image"
						:label="getString('image')"
						:max-bytes="300 * 1024"
						:max-width="900"
						:max-height="900"
						:files="category.picture ? [category.picture] : []"
						@upload-complete="pictureChanged"
						@removed-file="(url, newFiles) => category.picture = newFiles && newFiles.length ? newFiles[0] : null"
					/>
				</div>
				<div class="editor-column column-right mobile-60 align-start">
					<div class="availability-container">
						<AppSwitch :label="getString('available')" :checked="!!category.active || category.active === null" @switched="(value) => category.active = value" />
						<AppButtonIcon :active="category.active === null" prevent-change class="ml-10" icon-name="icon-hourglass" small @click="openTimetables" />
					</div>
				</div>
			</div>
		</div>

		<div class="editor-row all-buttons-container">
			<div class="buttons-container buttons-extra mt-20">
				<AppButton outline :text="getString('advanced_plural')" @click="advancedSheetActive = true" small />
				<AppButton outline :text="getString('options')" @click="optionsSheetActive = true" small />
			</div>
			<div class="buttons-container flex-end mt-20 mb-10">
				<div class="save-button-container">
					<AppButton :text="getString('save')" @click="saveCategories" />
					<AppClickableIcon :autoSetActive="false" class="delete-button" icon-name="iconBin" @click="deleteCategory" />
				</div>
			</div>
		</div>

	</div>
	<AppBottomSheet :force-width="bottomSheetWidth" :active="advancedSheetActive" @toggling-popup="toggleAdvancedSheet">
		<AdvancedCategoryEditor v-if="advancedSheetActive" :categoryTempId="categoryTempId" @save-clicked="saveAdvancedSheet" />
	</AppBottomSheet>
	<AppBottomSheet :force-width="bottomSheetWidth" :active="optionsSheetActive" @toggling-popup="toggleOptionsSheet">
		<OptionsCategoryEditor v-if="optionsSheetActive" :categoryTempId="categoryTempId" @save-clicked="saveOptionsSheet" />
	</AppBottomSheet>
	<AppBottomSheet :force-width="bottomSheetWidth" :active="timetablesSheetActive" @toggling-popup="toggleTimetablesSheet">
		<div class="timetables-content" v-if="timetablesSheetActive">
			<div class="row centered">
				<AppLoader v-if="loadingTimetables" />
			</div>
			<TimetablesEditor v-if="!loadingTimetables && loadedTimetables" :menu-category-id="category.menu_category_id ? Number(category.menu_category_id) : 0" :title="getString('timetable_title', [category.name])" with-quantity :timetables="timetables" @save-clicked="saveTimetables" />
		</div>
	</AppBottomSheet>
</template>

<script>
import AppTextInput from "@/shared/components/AppTextInput.vue";
import AppNumberInput from "@/shared/components/AppNumberInput.vue";
import AppSwitch from "@/shared/components/AppSwitch.vue";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import AppDropzone from "@/shared/components/AppDropzone.vue";
import AppColorPicker from "@/shared/components/AppColorPicker.vue";
import AppButton from "@/shared/components/AppButton.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import AdvancedCategoryEditor from "@/console-new/components/config/menu/AdvancedCategoryEditor.vue";
import TimetablesEditor from "@/console-new/components/shared/TimetablesEditor.vue";
import AppClickableIcon from "@/shared/components/AppClickableIcon.vue";
import AppLoader from "@/shared/components/AppLoader.vue";

import WindowWidth from "@/console-new/mixins/WindowWidth";
import {useLanguageStore, useNotificationStore} from "@/shared/stores";
import {useMenuStore} from "@/console-new/stores";

import {searchMainColors} from "@/shared/helpers/ui";
import OptionsCategoryEditor from "@/console-new/components/config/menu/OptionsCategoryEditor.vue";

export default {
	setup() {
		let languageStore = useLanguageStore();
		let menuStore = useMenuStore();
		let notificationStore = useNotificationStore();
		return {getString: languageStore.getString, showNotification: notificationStore.showNotification, menuStore};
	},
	name: "CategoryEditor",
	mixins: [WindowWidth],
	emits: ["category-saved"],
	components: {AppButton, AppColorPicker, AppTextInput, AppNumberInput, AppSwitch, AppButtonIcon, AppDropzone, AppBottomSheet, AdvancedCategoryEditor, OptionsCategoryEditor, TimetablesEditor, AppLoader, AppClickableIcon},
	data() {
		return {
			advancedSheetActive: false,
			optionsSheetActive: false,
			timetablesSheetActive: false,
			loadingTimetables: false,
			loadedTimetables: false,
			timetables: []
		};
	},
	props: {
		categoryTempId: {
			type: String,
			required: true
		}
	},
	computed: {
		category() {
			return this.menuStore.modMenu.categories[this.categoryTempId];
		},
		bottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else if(this.windowWidth < 1080) {
				return "720px";
			} else if(this.windowWidth < 1440) {
				return "960px";
			} else {
				return "1280px";
			}
		}
	},
	methods: {
		openTimetables() {
			if(this.category.menu_category_id) {
				this.timetablesSheetActive = true;
				this.loadingTimetables = true;
				this.loadedTimetables = false;
				let self = this;
				this.menuStore.getCategoryTimetables(this.category.menu_category_id, function(timetables) {
					self.loadingTimetables = false;
					self.loadedTimetables = true;
					self.timetables = timetables;
				});
			} else {
				this.showNotification("timetable-save-category");
			}
		},
		saveCategories(notify = true) {
			let self = this;
			this.menuStore.saveCategories(function (success, details) {
				if(success) {
					notify && self.showNotification("category-saved");
					self.$emit("category-saved");
				} else {
					if(details === "auth_denied") {
						self.showNotification("auth-denied-error");
					} else {
						self.showNotification("generic-fail");
					}
				}
			});
		},
		deleteCategory() {
			for(let iI in this.menuStore.modMenu.categories[this.categoryTempId].items) {
				let item = this.menuStore.modMenu.categories[this.categoryTempId].items[iI];
				item.menu_category_id = null;
				this.menuStore.modMenu.orphanItems[item.tempid] = item;
			}
			delete this.menuStore.modMenu.categories[this.categoryTempId];
		},
		toggleAdvancedSheet(isOpen) {
			if(!isOpen) {
				this.advancedSheetActive = false;
			}
		},
		toggleOptionsSheet(isOpen) {
			if(!isOpen) {
				this.optionsSheetActive = false;
			}
		},
		saveTimetables() {
			this.category.active = null;
			this.toggleTimetablesSheet(false);
			this.saveCategories(false);
		},
		toggleTimetablesSheet(isOpen) {
			if (!isOpen) {
				this.timetablesSheetActive = false;
				this.loadedTimetables = false;
			}
		},
		saveAdvancedSheet(variationsOnly) {
			this.advancedSheetActive = false;
			this.$nextTick(() => {
				this.category.variations_only = !!variationsOnly;
				this.saveCategories();
			});
		},
		saveOptionsSheet() {
			this.optionsSheetActive = false;
			this.saveCategories();
		},
		pictureChanged(urls, files) {
			this.category.picture = urls[0];

			let self = this;
			if(files[0] && files[0].dataURL) {
				searchMainColors(files[0].dataURL, function (color) {
					self.category.color = color;
				});
			}
		},
	}
}
</script>

<style lang="scss" scoped>
.category-editor {
	margin: 0 auto;
}
.editor-row-half {
	display: flex;
	justify-content: space-between;
	width: 100%;

	&.editor-row-category-first {
		@media (max-width: 1290px) {
			flex-direction: column;
			gap: 15px;
		}
	}

	.column-right {
		display: flex;
		justify-content: flex-end;
	}

	.editor-column {
		width: 50%;
	}

	.editor-column-40 {
		width: 40%;
	}

	.editor-column-60 {
		width: 60%;
	}

	.editor-row-half-half {
		display: flex;
		justify-content: space-between;
	}

	.padding-large-right {
		padding-right: 40px;

		.editor-input {
			padding-right: 30px;
		}

		@media (max-width: 1650px) {
			padding-right: 20px;
		}

		@media (max-width: 1500px) {
			padding-right: 15px;
		}

		@media (max-width: 1290px) {
			padding-right: 0;
		}
	}

	.padding-large-left {
		padding-left: 40px;

		@media (max-width: 1650px) {
			padding-left: 20px;
		}

		@media (max-width: 1500px) {
			padding-left: 15px;
		}

		@media (max-width: 1290px) {
			padding-left: 0;
		}
	}

	.availability-container {
		display: flex;
		justify-content: center;
		align-items: flex-end;
	}

	.editor-input {
		padding-right: 15px;
		max-width: 280px;
		width: 100%;
	}


}

.all-buttons-container {
	display: flex;
	justify-content: space-between;

	@media (max-width: 1050px) {
		flex-direction: column;
	}

	@media (max-width: 920px) {
		flex-direction: row;
	}

	@media (max-width: 520px) {
		flex-direction: column;
	}
}
.buttons-container {
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	width: 100%;
	gap: 10px;

	&.flex-end {
		justify-content: flex-end;
	}

	&.buttons-extra {
		justify-content: flex-start;

		@media (max-width: 1050px) {
			justify-content: space-between;
		}

		@media (max-width: 920px) {
			justify-content: flex-start;
		}

		@media (max-width: 520px) {
			justify-content: space-between;
		}

	}

	.save-button-container {
		display: flex;
		align-items: center;

		.base-button {
			margin-right: 8px;
		}

		.delete-button {
			width: 20px;
			height: 20px;
			display: flex;

			:deep(svg) {
				padding: 3px;
			}
		}
	}

}

</style>